/**
 * Stock → Stock issues: stock that leaves without a sale (samples, photoshoots, influencers, staff) and what comes back.
 * Never revenue. Unit cost and stock value show to admins only; the warehouse role can issue and record returns,
 * reversing is for managers and admins.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Download, Plus, RefreshCw, Trash2, Undo2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { useChannels } from '../hooks/useChannels';
import { canEditModule, isAdmin, isWarehouseRole, viewOnlyHint } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LoadError, errorMessage } from '../components/LoadError';
import { indiaToday, number, wholeMoney } from '../components/sales/format';
import { useDebouncedValue } from '../components/sales/useAbortableGet';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const STATUS_LABEL = { OPEN: 'Out', PARTIAL: 'Partly back', CLOSED: 'Closed', REVERSED: 'Reversed' };
const STATUS_TONE = { OPEN: 'bg-sky-50 text-sky-800', PARTIAL: 'bg-amber-50 text-amber-800', CLOSED: 'bg-gray-100 text-gray-700', REVERSED: 'bg-gray-100 text-gray-500 line-through' };
const KIND_LABEL = { issue: 'Issued', return: 'Returned to stock', damaged: 'Damaged', kept: 'Kept', lost: 'Lost', reverse: 'Reversed', b2b_dispatch: 'B2B dispatch', b2b_undo: 'B2B dispatch undone' };
const OUTCOMES = [['restock', 'Back in stock'], ['damaged', 'Damaged'], ['kept', 'Kept'], ['lost', 'Lost']];
const SELECT = 'h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm';

const Tile = ({ label, value, tone = 'text-gray-900' }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`mt-1 text-lg font-semibold tabular-nums ${tone}`}>{value}</p>
  </div>
);

const emptyForm = outlet => ({ channel: outlet || '', from_bin: 'warehouse', counterparty: '', handled_by: '', reason: '', note: '', issue_date: indiaToday(), expected_return: '', lines: [{ sku_code: '', qty: 1, price_received: '' }] });

const NewIssueDialog = ({ open, onOpenChange, country, onDone }) => {
  const master = useChannels();
  const outlets = useMemo(() => master.internal.filter(c => c.status === 'active'), [master.internal]);
  const [form, setForm] = useState(emptyForm());
  const [skus, setSkus] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Only when the dialog opens: the channel list refreshing behind it must not wipe what has been typed
  const defaultOutlet = outlets[0]?.code;
  useEffect(() => {
    if (open && defaultOutlet) setForm(current => (current.channel ? current : { ...current, channel: defaultOutlet }));
  }, [open, defaultOutlet]);
  useEffect(() => {
    if (!open) return undefined;
    setForm(emptyForm());
    setError('');
    const controller = new AbortController();
    axios.get(`${API}/skus?country=${country}`, { signal: controller.signal })
      .then(res => setSkus(Array.isArray(res.data) ? res.data : (res.data?.items || [])))
      .catch(() => {});
    return () => controller.abort();
  }, [open, country]);
  const outlet = master.byCode[form.channel];
  const terms = outlet?.issue || {};
  const set = patch => setForm(current => ({ ...current, ...patch }));
  const setLine = (index, patch) => set({ lines: form.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) });
  const submit = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API}/stock-issues`, { ...form, country, expected_return: terms.returnable ? form.expected_return || null : null,
        lines: form.lines.map(line => ({ sku_code: line.sku_code.trim(), qty: Number(line.qty), price_received: !terms.price_received_allowed || line.price_received === '' ? null : Number(line.price_received) })) });
      toast.success('Stock issued');
      onOpenChange(false);
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'Could not save this issue'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Issue stock</DialogTitle>
          <DialogDescription>Stock leaves the bin now. This is never counted as a sale or as revenue.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="stock-issue-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1"><Label htmlFor="si-channel">Issued for</Label>
              <select id="si-channel" className={SELECT} value={form.channel} onChange={e => set({ channel: e.target.value })} required data-testid="si-channel">
                {outlets.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select></div>
            <div className="space-y-1"><Label htmlFor="si-bin">From bin</Label>
              <select id="si-bin" className={SELECT} value={form.from_bin} onChange={e => set({ from_bin: e.target.value })} data-testid="si-bin">
                {(master.bins.length ? master.bins : [{ key: 'warehouse', label: 'Warehouse' }]).map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select></div>
            <div className="space-y-1"><Label htmlFor="si-to">Given to</Label>
              <Input id="si-to" value={form.counterparty} onChange={e => set({ counterparty: e.target.value })} placeholder="Person or company" required maxLength={120} data-testid="si-to" /></div>
            <div className="space-y-1"><Label htmlFor="si-by">Handled by</Label>
              <Input id="si-by" value={form.handled_by} onChange={e => set({ handled_by: e.target.value })} placeholder="You, if left blank" maxLength={120} /></div>
            <div className="space-y-1"><Label htmlFor="si-date">Issue date</Label>
              <Input id="si-date" type="date" max={indiaToday()} value={form.issue_date} onChange={e => set({ issue_date: e.target.value })} required /></div>
            {terms.returnable ? (
              <div className="space-y-1"><Label htmlFor="si-back">Expected back</Label>
                <Input id="si-back" type="date" min={form.issue_date} value={form.expected_return} onChange={e => set({ expected_return: e.target.value })} />
                <p className="text-[11px] text-gray-500">Blank means {terms.default_return_days || 30} days after the issue date.</p></div>
            ) : <p className="self-end text-[12px] text-gray-500">This outlet keeps the stock, so the issue closes straight away.</p>}
          </div>
          <div className="space-y-1"><Label htmlFor="si-reason">Reason</Label>
            <Input id="si-reason" value={form.reason} onChange={e => set({ reason: e.target.value })} placeholder="What it is for" required maxLength={500} data-testid="si-reason" /></div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Products</legend>
            <datalist id="si-skus">{skus.map(s => <option key={s.sku_code} value={s.sku_code}>{s.product_name}</option>)}</datalist>
            {form.lines.map((line, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="flex items-center gap-2">
                <Input list="si-skus" aria-label="SKU" value={line.sku_code} onChange={e => setLine(index, { sku_code: e.target.value })} placeholder="SKU" required className="flex-1" data-testid={`si-sku-${index}`} />
                <Input aria-label="Units" type="number" min="1" step="1" value={line.qty} onChange={e => setLine(index, { qty: e.target.value })} required className="w-20" data-testid={`si-qty-${index}`} />
                {terms.price_received_allowed && <Input aria-label="Amount received" type="number" min="0" step="0.01" value={line.price_received} onChange={e => setLine(index, { price_received: e.target.value })} placeholder="₹ received" className="w-28" />}
                <Button type="button" variant="ghost" size="sm" aria-label="Remove product" disabled={form.lines.length === 1} onClick={() => set({ lines: form.lines.filter((_, i) => i !== index) })}><Trash2 className="h-4 w-4" aria-hidden="true" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => set({ lines: [...form.lines, { sku_code: '', qty: 1, price_received: '' }] })}><Plus className="mr-1 h-4 w-4" aria-hidden="true" />Add product</Button>
            {terms.price_received_allowed && <p className="text-[11px] text-gray-500">The amount received is recorded only. It is not revenue. Enter the total for the line.</p>}
          </fieldset>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !outlets.length} data-testid="si-save">{saving ? 'Saving…' : 'Issue stock'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const stillOut = line => line.qty - OUTCOMES.reduce((sum, [key]) => sum + Number(line.outcomes?.[key] || 0), 0);

const ReturnDialog = ({ issue, onClose, country, onDone }) => {
  const master = useChannels();
  const [rows, setRows] = useState({});
  const [day, setDay] = useState(indiaToday());
  const [toBin, setToBin] = useState('warehouse');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!issue) return;
    setRows({});
    setDay(indiaToday());
    setToBin(issue.from_bin);
    setNote('');
    setError('');
  }, [issue]);
  if (!issue) return null;
  const value = (lineId, key) => rows[lineId]?.[key] ?? '';
  const change = (lineId, key, next) => setRows(current => ({ ...current, [lineId]: { ...current[lineId], [key]: next } }));
  const submit = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API}/stock-issues/${issue.id}/return`, { country, date: day, to_bin: toBin, note,
        items: Object.entries(rows).map(([line_id, counts]) => ({ line_id, ...Object.fromEntries(OUTCOMES.map(([key]) => [key, Number(counts[key] || 0)])) })) });
      toast.success('Recorded');
      onClose();
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'Could not record this'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={next => { if (!next) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record what happened · {issue.id}</DialogTitle>
          <DialogDescription>Given to {issue.counterparty}. Only units entered as back in stock return to a bin.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1"><Label htmlFor="ret-date">Date</Label><Input id="ret-date" type="date" min={issue.issue_date} max={indiaToday()} value={day} onChange={e => setDay(e.target.value)} required /></div>
            <div className="space-y-1"><Label htmlFor="ret-bin">Back into bin</Label>
              <select id="ret-bin" className={SELECT} value={toBin} onChange={e => setToBin(e.target.value)}>
                {(master.bins.length ? master.bins : [{ key: 'warehouse', label: 'Warehouse' }]).map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500"><th className="py-1 pr-2">SKU</th><th className="py-1 pr-2 text-right">Still out</th>{OUTCOMES.map(([key, text]) => <th key={key} className="py-1 pr-2">{text}</th>)}</tr></thead>
              <tbody>
                {issue.lines.filter(line => stillOut(line) > 0).map(line => (
                  <tr key={line.line_id} className="border-t border-gray-100">
                    <td className="py-1.5 pr-2 font-mono text-xs">{line.sku_code}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">{stillOut(line)}</td>
                    {OUTCOMES.map(([key, text]) => (
                      <td key={key} className="py-1.5 pr-2"><Input aria-label={`${text}: ${line.sku_code}`} type="number" min="0" max={stillOut(line)} step="1" value={value(line.line_id, key)} onChange={e => change(line.line_id, key, e.target.value)} className="h-8 w-20" data-testid={`ret-${key}-${line.line_id}`} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-1"><Label htmlFor="ret-note">Note</Label><Input id="ret-note" value={note} onChange={e => setNote(e.target.value)} maxLength={1000} /></div>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="ret-save">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ReverseDialog = ({ issue, onClose, country, onDone }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setReason(''); setError(''); }, [issue]);
  if (!issue) return null;
  const submit = async event => {
    event.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/stock-issues/${issue.id}/reverse`, { country, reason });
      toast.success('Issue reversed. The stock is back.');
      onClose();
      onDone();
    } catch (err) {
      setError(errorMessage(err, 'Could not reverse this issue'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={next => { if (!next) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reverse {issue.id}?</DialogTitle>
          <DialogDescription>For an issue entered by mistake. All {number(issue.units)} units go back to the bin they came from.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1"><Label htmlFor="rev-reason">Why</Label><Input id="rev-reason" value={reason} onChange={e => setReason(e.target.value)} required maxLength={500} data-testid="rev-reason" /></div>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Keep it</Button>
            <Button type="submit" variant="destructive" disabled={saving} data-testid="rev-save">{saving ? 'Reversing…' : 'Reverse issue'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const IssueTable = ({ state, country, canEdit, canReverse, showCost, stamp, onChanged, onReturn, onReverse }) => {
  const master = useChannels();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [channel, setChannel] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const query = useDebouncedValue(search, 300);
  useEffect(() => { setPage(1); }, [channel, query, state, country]);
  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    axios.get(`${API}/stock-issues`, { params: { country, state, channel: channel || undefined, search: query || undefined, page }, signal: controller.signal })
      .then(res => {
        setData(res.data);
        const last = Math.max(1, Math.ceil(res.data.total / res.data.page_size));
        if (page > last) setPage(last);    // the page emptied, e.g. the last issue on it just closed
      })
      .catch(err => { if (!axios.isCancel(err)) setError(err); });
    return () => controller.abort();
  }, [country, state, channel, query, page, stamp]);
  if (error) return <LoadError error={error} onRetry={onChanged} />;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Input aria-label="Search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search person, SKU, reason" className="h-9 w-64" data-testid="si-search" />
        <select aria-label="Outlet" className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" value={channel} onChange={e => setChannel(e.target.value)}>
          <option value="">All outlets</option>
          {master.internal.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm" data-testid="stock-issues-table">
          <thead className="bg-gray-50 text-left text-xs text-gray-600">
            <tr><th className="p-3">Issue</th><th className="p-3">Date</th><th className="p-3">Outlet</th><th className="p-3">Given to</th><th className="p-3">Products</th><th className="p-3 text-right">Units</th><th className="p-3 text-right">Still out</th><th className="p-3">Expected back</th><th className="p-3">Status</th>{showCost && <th className="p-3 text-right">Value at cost</th>}<th className="p-3" /></tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan="11" className="p-6 text-center text-gray-500">Loading…</td></tr>}
            {data && !data.items.length && <tr><td colSpan="11" className="p-6 text-center text-gray-500">Nothing here.</td></tr>}
            {data?.items.map(item => (
              <tr key={item.id} className="border-t border-gray-100 align-top">
                <td className="p-3 font-mono text-xs" title={item.reason}>{item.id}</td>
                <td className="p-3 whitespace-nowrap">{item.issue_date}</td>
                <td className="p-3">{master.byCode[item.channel]?.name || item.channel_name}</td>
                <td className="p-3">{item.counterparty}<div className="text-xs text-gray-500">{item.reason}</div></td>
                <td className="p-3 font-mono text-xs">{item.lines.map(line => `${line.sku_code} × ${line.qty}${line.entered_sku ? ` (from ${line.entered_sku})` : ''}`).join(', ')}{item.price_received > 0 && <div className="font-sans text-gray-500">Received {wholeMoney(item.price_received)}</div>}</td>
                <td className="p-3 text-right tabular-nums">{number(item.units)}</td>
                <td className="p-3 text-right tabular-nums">{number(item.open_units)}</td>
                <td className="p-3 whitespace-nowrap">{item.expected_return || '—'}{item.overdue_days ? <div className="text-xs font-medium text-red-700">{item.overdue_days} days late</div> : null}</td>
                <td className="p-3"><span className={`rounded px-1.5 py-0.5 text-xs ${STATUS_TONE[item.status] || ''}`}>{STATUS_LABEL[item.status] || item.status}</span></td>
                {showCost && <td className="p-3 text-right tabular-nums">{wholeMoney(item.cost_value)}</td>}
                <td className="p-3 whitespace-nowrap text-right">
                  {canEdit && item.open_units > 0 && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onReturn(item)} data-testid={`si-return-${item.id}`}>Record return</Button>}
                  {canReverse && item.status !== 'REVERSED' && !item.returns?.length && <Button variant="ghost" size="sm" className="ml-1 h-7 text-xs" onClick={() => onReverse(item)} data-testid={`si-reverse-${item.id}`}><Undo2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />Reverse</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <span>Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
};

const MovementsTable = ({ country, stamp }) => {
  const master = useChannels();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sku, setSku] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [country]);
  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    axios.get(`${API}/stock-issues/movements`, { params: { country, sku: sku || undefined, page }, signal: controller.signal })
      .then(res => setData(res.data))
      .catch(err => { if (!axios.isCancel(err)) setError(err); });
    return () => controller.abort();
  }, [country, sku, page, stamp]);
  if (error) return <LoadError error={error} />;
  const place = key => master.byCode[key]?.name || master.bins.find(b => b.key === key)?.label || KIND_LABEL[key] || key;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  return (
    <div className="space-y-3">
      <Input aria-label="SKU" value={sku} onChange={e => { setSku(e.target.value); setPage(1); }} placeholder="Exact SKU code" className="h-9 w-64" />
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm" data-testid="stock-movements-table">
          <thead className="bg-gray-50 text-left text-xs text-gray-600"><tr><th className="p-3">Date</th><th className="p-3">SKU</th><th className="p-3">What</th><th className="p-3 text-right">Units</th><th className="p-3">From</th><th className="p-3">To</th><th className="p-3">Issue</th><th className="p-3">By</th></tr></thead>
          <tbody>
            {!data && <tr><td colSpan="8" className="p-6 text-center text-gray-500">Loading…</td></tr>}
            {data && !data.items.length && <tr><td colSpan="8" className="p-6 text-center text-gray-500">No movements yet.</td></tr>}
            {data?.items.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${row.issue_id}-${row.at}-${row.sku_code}-${row.kind}-${row.to}-${index}`} className="border-t border-gray-100">
                <td className="p-3 whitespace-nowrap">{row.date}</td><td className="p-3 font-mono text-xs">{row.sku_code}</td><td className="p-3">{KIND_LABEL[row.kind] || row.kind}</td>
                <td className="p-3 text-right tabular-nums">{number(row.qty)}</td><td className="p-3">{place(row.from)}</td><td className="p-3">{place(row.to)}</td>
                <td className="p-3 font-mono text-xs">{row.issue_id}</td><td className="p-3 text-xs text-gray-600">{row.by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && <div className="flex items-center justify-end gap-2 text-sm"><Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button><span>Page {page} of {pages}</span><Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button></div>}
    </div>
  );
};

export default function StockIssuesPage() {
  const { user } = useAuth();
  const { country } = useCountry();
  const [stamp, setStamp] = useState(0);
  const [summary, setSummary] = useState(null);
  const [creating, setCreating] = useState(false);
  const [returning, setReturning] = useState(null);
  const [reversing, setReversing] = useState(null);
  const canEdit = canEditModule(user, 'stock_issues');
  const showCost = isAdmin(user);
  const canReverse = canEdit && !isWarehouseRole(user);
  const refresh = useCallback(() => setStamp(value => value + 1), []);
  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${API}/stock-issues/summary`, { params: { country }, signal: controller.signal })
      .then(res => setSummary(res.data)).catch(() => {});
    return () => controller.abort();
  }, [country, stamp]);
  const download = async () => {
    try {
      const { data } = await axios.get(`${API}/stock-issues/export`, { params: { country }, responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'stock_issues.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not download'));
    }
  };
  const tableProps = { country, canEdit, canReverse, showCost, stamp, onChanged: refresh, onReturn: setReturning, onReverse: setReversing };
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="stock-issues-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Stock issues</h1>
          <p className="text-sm text-gray-600">Samples, photoshoots, influencers and staff: stock that leaves without a sale, and what comes back. Never counted as revenue.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refresh} aria-label="Refresh"><RefreshCw className="h-4 w-4" aria-hidden="true" /></Button>
          <Button variant="outline" size="sm" onClick={download} data-testid="si-export"><Download className="mr-1 h-4 w-4" aria-hidden="true" />Export</Button>
          <Button size="sm" disabled={!canEdit} title={canEdit ? undefined : viewOnlyHint(user)} onClick={() => setCreating(true)} data-testid="si-new"><Plus className="mr-1 h-4 w-4" aria-hidden="true" />Issue stock</Button>
        </div>
      </div>
      <div className={`grid grid-cols-2 gap-3 ${showCost ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
        <Tile label="Issues still out" value={number(summary?.open_issues)} />
        <Tile label="Units still out" value={number(summary?.open_units)} />
        <Tile label="Units overdue" value={number(summary?.overdue_units)} tone={summary?.overdue_units ? 'text-red-700' : 'text-gray-900'} />
        {showCost && <Tile label="Stock given, at cost" value={wholeMoney(summary?.cost_value)} />}
        <Tile label="Received from staff" value={wholeMoney(summary?.price_received)} />
      </div>
      {showCost && summary?.uncosted_units > 0 && (
        <p className="text-xs text-amber-800" data-testid="si-uncosted">{number(summary.uncosted_units)} unit{summary.uncosted_units === 1 ? ' has' : 's have'} no purchase cost, so the figure at cost is low.</p>
      )}
      {summary?.overdue_issues > 0 && (
        <p className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="status">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />{number(summary.overdue_issues)} issue{summary.overdue_issues === 1 ? ' is' : 's are'} past the expected return date.
        </p>
      )}
      <Tabs defaultValue="open">
        <TabsList>
          <TabsTrigger value="open" data-testid="si-tab-open">Still out</TabsTrigger>
          <TabsTrigger value="all" data-testid="si-tab-all">All issues</TabsTrigger>
          <TabsTrigger value="movements" data-testid="si-tab-movements">Movements</TabsTrigger>
        </TabsList>
        <TabsContent value="open" className="mt-3"><IssueTable state="open" {...tableProps} /></TabsContent>
        <TabsContent value="all" className="mt-3"><IssueTable state="all" {...tableProps} /></TabsContent>
        <TabsContent value="movements" className="mt-3"><MovementsTable country={country} stamp={stamp} /></TabsContent>
      </Tabs>
      <NewIssueDialog open={creating} onOpenChange={setCreating} country={country} onDone={refresh} />
      <ReturnDialog issue={returning} onClose={() => setReturning(null)} country={country} onDone={refresh} />
      <ReverseDialog issue={reversing} onClose={() => setReversing(null)} country={country} onDone={refresh} />
    </div>
  );
}
